import StatsVideoReference from '../../components/Review/StatsVideoReference';
import EventSetup from "../../components/planning/EventSetup";
import useEventSetupLifecycle from "../../components/planning/useEventSetupLifecycle";
import {attachSetupStats} from "../../utils/eventSetupStore";
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./UploadScoreDialog.css";
import { auth, db, storage, functions } from "../../Security/firebase-config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  collection,
  getDocs,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ensureActiveTeamClaim } from "../../utils/authClaims";
import {
  checkTeamQuotaAllowance,
  confirmTrackedUploadFinalize,
  triggerTeamQuotaRefresh,
} from "../../utils/teamQuota";

import CustomAlert from "../../components/core-components/CustomAlert";







const MAP_NAMES = [
  "ABYSS",
  "ASCENT",
  "BIND",
  "BREEZE",
  "CORRODE",
  "FRACTURE",
  "HAVEN",
  "ICEBOX",
  "LOTUS",
  "PEARL",
  "SPLIT",
  "SUNSET",
];

function titleCaseWord(s) {
  const lower = String(s || "").toLowerCase();
  return lower ? lower[0].toUpperCase() + lower.slice(1) : "";
}

function extractMapFromOcrText(text) {
  const norm = String(text || "")
    .normalize("NFKC")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .toUpperCase();

  // Prefer "MAP <name>"
  const re = new RegExp(`\\bMAP\\b\\D{0,20}\\b(${MAP_NAMES.join("|")})\\b`, "i");
  const m = norm.match(re);
  if (m?.[1]) return titleCaseWord(m[1]);

  // Fallback: any map name anywhere
  for (const name of MAP_NAMES) {
    if (norm.includes(name)) return titleCaseWord(name);
  }

  return null;
}

const MONTHS = {
  JAN: "01",
  FEB: "02",
  MAR: "03",
  APR: "04",
  MAY: "05",
  JUN: "06",
  JUL: "07",
  AUG: "08",
  SEP: "09",
  SEPT: "09",
  OCT: "10",
  NOV: "11",
  DEC: "12",
};

function extractDateISOFromOcrText(text) {
  const norm = String(text || "")
    .normalize("NFKC")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .toUpperCase();

  // Matches: "FEB 6, 2026" (commas/spaces vary)
  const m = norm.match(
    /\b(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|SEPT|OCT|NOV|DEC)\b\D{0,6}\b(\d{1,2})\b\D{0,6}\b(\d{4})\b/
  );
  if (!m) return null;

  const mon = m[1];
  const day = Number(m[2]);
  const year = Number(m[3]);

  const mm = MONTHS[mon];
  if (!mm) return null;
  if (Number.isNaN(day) || day < 1 || day > 31) return null;
  if (Number.isNaN(year) || year < 2020 || year > 2100) return null;

  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`; // YYYY-MM-DD
}


/* ---------------------------------------------
   IMAGE HELPERS (with optional crop support)
----------------------------------------------*/

// Draw an image to canvas with optional grayscale/contrast and optional CROP in natural pixels.
// Returns base64 (no data: prefix).
async function preprocessToBase64(
  file,
  {
    maxWidth = 1600, // keep big enough for text clarity
    grayscale = true,
    contrast = 1.25, // >1 increases contrast slightly
    quality = 0.85, // JPEG quality
  } = {},
  crop // {x, y, w, h} in NATURAL image pixels (optional)
) {
  const img = await fileToImage(file);

  const srcX = crop?.x ?? 0;
  const srcY = crop?.y ?? 0;
  const srcW = crop?.w ?? (img.naturalWidth || img.width);
  const srcH = crop?.h ?? (img.naturalHeight || img.height);

  const scale = srcW > maxWidth ? maxWidth / srcW : 1;
  const dstW = Math.round(srcW * scale);
  const dstH = Math.round(srcH * scale);

  const canvas = document.createElement("canvas");
  canvas.width = dstW;
  canvas.height = dstH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, dstW, dstH);

  if (grayscale) {
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = imgData.data;
    for (let i = 0; i < d.length; i += 4) {
      const y = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      let v = y * contrast;
      if (v > 255) v = 255;
      d[i] = d[i + 1] = d[i + 2] = v;
    }
    ctx.putImageData(imgData, 0, 0);
  }

  // Better for OCR: JPEG + real quality control
  const dataUrl = canvas.toDataURL("image/jpeg", quality);
  return dataUrl.split(",")[1];

}

function fileToImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/* ---------------------------------------------
   ROUND / PISTOL HELPERS
----------------------------------------------*/

const makeEmptyRounds = () => ({
  atk: { r1: null, r2: null, r3: null },
  def: { r1: null, r2: null, r3: null },
});

const ROUND_SLOT_DEFS = [
  { key: "r1", label: "Round 1", strategyPlaceholder: "Add pistol strategy tag" },
  { key: "r2", label: "Round 2", strategyPlaceholder: "Add round strategy tag" },
  { key: "r3", label: "Round 3", strategyPlaceholder: "Add round strategy tag" },
];

const makeEmptyRoundTags = () => ({
  atk: { r1: [], r2: [], r3: [] },
  def: { r1: [], r2: [], r3: [] },
});

const getRoundTagInputKey = (side, roundKey) => `${side}:${roundKey}`;

const makeEmptyRoundTagInputs = () =>
  ROUND_SLOT_DEFS.reduce(
    (acc, { key }) => ({
      ...acc,
      [getRoundTagInputKey("atk", key)]: "",
      [getRoundTagInputKey("def", key)]: "",
    }),
    {}
  );

function normalizeBoolOrNull(v) {
  if (v === true) return true;
  if (v === false) return false;
  return null;
}

function normalizeRoundTagValue(tag) {
  return String(tag || "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeRoundTagList(tags) {
  const next = [];
  const seen = new Set();

  (Array.isArray(tags) ? tags : []).forEach((tag) => {
    const normalized = normalizeRoundTagValue(tag);
    if (!normalized) return;

    const dedupeKey = normalized.toLowerCase();
    if (seen.has(dedupeKey)) return;

    seen.add(dedupeKey);
    next.push(normalized);
  });

  return next;
}

function splitRoundTagInput(value) {
  return String(value || "")
    .split(/[,\n]/)
    .map(normalizeRoundTagValue)
    .filter(Boolean);
}

function normalizeStoredRoundTags(rawRoundTags) {
  const next = makeEmptyRoundTags();

  ["atk", "def"].forEach((side) => {
    ROUND_SLOT_DEFS.forEach(({ key }) => {
      next[side][key] = normalizeRoundTagList(rawRoundTags?.[side]?.[key]);
    });
  });

  return next;
}

function mergePendingRoundTagInputs(baseRoundTags, pendingInputs) {
  const merged = normalizeStoredRoundTags(baseRoundTags);

  ["atk", "def"].forEach((side) => {
    ROUND_SLOT_DEFS.forEach(({ key }) => {
      const pendingTags = splitRoundTagInput(
        pendingInputs?.[getRoundTagInputKey(side, key)] || ""
      );
      if (!pendingTags.length) return;

      merged[side][key] = normalizeRoundTagList([
        ...merged[side][key],
        ...pendingTags,
      ]);
    });
  });

  return merged;
}

function collectSuggestedRoundTags(matches) {
  const deduped = new Map();

  (Array.isArray(matches) ? matches : []).forEach((match) => {
    const roundTags = normalizeStoredRoundTags(match?.roundTags);

    ["atk", "def"].forEach((side) => {
      ROUND_SLOT_DEFS.forEach(({ key }) => {
        roundTags[side][key].forEach((tag) => {
          const dedupeKey = tag.toLowerCase();
          if (!deduped.has(dedupeKey)) {
            deduped.set(dedupeKey, tag);
          }
        });
      });
    });
  });

  return Array.from(deduped.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
}

/*
function RoundTagEditor({
  inputId,
  inputName,
  placeholder,
  committedTags,
  suggestionPool,
  onAddTags,
  onRemoveTag,
  onDraftChange,
  resetToken,
}) {
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const draftChangeRef = useRef(onDraftChange);

  useEffect(() => {
    draftChangeRef.current = onDraftChange;
  }, [onDraftChange]);

  useEffect(() => {
    setInputValue("");
    draftChangeRef.current("");
  }, [resetToken]);

  const query = normalizeRoundTagValue(inputValue).toLowerCase();
  const committedTagList = Array.isArray(committedTags) ? committedTags : [];

  const suggestions = useMemo(
    () => {
      const selected = new Set(
        committedTagList.map((tag) => normalizeRoundTagValue(tag).toLowerCase())
      );

      return (Array.isArray(suggestionPool) ? suggestionPool : [])
        .filter((tag) => !selected.has(normalizeRoundTagValue(tag).toLowerCase()))
        .filter((tag) => query && normalizeRoundTagValue(tag).toLowerCase().includes(query))
        .slice(0, 6);
    },
    [committedTagList, query, suggestionPool]
  );

  const commitInput = () => {
    const parsedTags = splitRoundTagInput(inputValue);
    if (!parsedTags.length) return;
    onAddTags(parsedTags);
    setInputValue("");
    onDraftChange("");
  };

  return (
    <div className="ts-round-tags">
      {committedTags.length > 0 && (
        <div className="ts-round-tag-list">
          {committedTags.map((tag) => (
            <span key={tag} className="ts-round-tag-chip">
              <span>{tag}</span>
              <button
                type="button"
                className="ts-round-tag-chip__remove"
                aria-label={`Remove ${tag}`}
                onClick={() => onRemoveTag(tag)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <textarea
        id={inputId}
        name={inputName}
        className="ts-round-tag-input"
        value={inputValue}
        placeholder={placeholder}
        rows={2}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => {
          const nextValue = e.target.value;
          setInputValue(nextValue);
          onDraftChange(nextValue);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          window.setTimeout(() => {
            setIsFocused(false);
          }, 120);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commitInput();
          }
        }}
      />

      {isFocused && suggestions.length > 0 && (
        <div className="ts-round-tag-suggestions">
          {suggestions.map((tag) => (
            <button
              key={tag}
              type="button"
              className="ts-round-tag-suggestion"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onAddTags([tag]);
                setInputValue("");
                onDraftChange("");
              }}
            >
              {tag}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

*/
function pistolCountFromRounds(roundWins) {
  const atk = roundWins?.atk?.r1;
  const def = roundWins?.def?.r1;
  const a = atk === true ? 1 : 0;
  const d = def === true ? 1 : 0;
  return a + d; // 0..2
}

function formatCaptureTime(sec) {
  const n = Number(sec);
  if (!Number.isFinite(n) || n < 0) return "";

  const hours = Math.floor(n / 3600);
  const minutes = Math.floor((n % 3600) / 60);
  const seconds = n % 60;

  const fixed = seconds.toFixed(3);
  const [wholeSeconds, ms] = fixed.split(".");
  const paddedSeconds = String(wholeSeconds).padStart(2, "0");
  const paddedMinutes = String(minutes).padStart(2, "0");

  if (hours > 0) {
    return `${hours}:${paddedMinutes}:${paddedSeconds}.${ms}`;
  }

  return `${minutes}:${paddedSeconds}.${ms}`;
}

/* ---------------------------------------------
   COMPONENT
----------------------------------------------*/

export default function UploadScoreDialog({
  teamId,
  initialSetupEventId = "",
  savedStatsId = null,
  onClose,
  existing = null,

  referenceVideoUrl = null,
  initialImageUrl = null,
  initialImageBlob = null,
  hideFileInput = false,

  linkedVideoMeta = null,

  onSaved = null,
  onAttachExisting = null,

  // NEW
  disableImageUpload = false,
  initialMatchDateISO = "",
  draftState = null,
  onDraftChange = null,
}) {
  const isEdit = !!existing;
  const setupLifecycle = useEventSetupLifecycle();
  const [setupEventId, setSetupEventId] = useState(existing?.calendarEventId || initialSetupEventId);
  const setupEventIdRef = useRef(setupEventId);
  setupEventIdRef.current = setupEventId;
  const [setupReady, setSetupReady] = useState(false);
  const [sharedPlayers, setSharedPlayers] = useState(null);
  async function persistSetupStats(target, payload) {
    await attachSetupStats({teamId, key: setupEventId, statsId: target.id, payload});
    return target;
  }

  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const generatedFrameFileRef = useRef(null);
  const previewObjectUrlRef = useRef(null);
  const didInitRef = useRef(false);
  const lastDraftPayloadRef = useRef("");
  const linkedVideoComp = linkedVideoMeta?.comp;
  const linkedVideoMapId = linkedVideoMeta?.mapId ?? "";
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confidence, setConfidence] = useState(null);
  const [raw, setRaw] = useState("");
  const [ocrDebug, setOcrDebug] = useState(null);
  const [ocrParsed, setOcrParsed] = useState(null);
  const [ocrMeta, setOcrMeta] = useState(null);

  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [fields, setFields] = useState(() => ({
    us: draftState?.fields?.us ?? "",
    them: draftState?.fields?.them ?? "",
    opponent: draftState?.fields?.opponent ?? "",
    map: draftState?.fields?.map ?? linkedVideoMapId,
    gameType: draftState?.fields?.gameType ?? "Scrims",
    matchDateISO: draftState?.fields?.matchDateISO ?? "",
    comp:
      draftState?.fields?.comp ??
      (Array.isArray(linkedVideoComp)
        ? [...linkedVideoComp.slice(0, 5), "", "", "", "", ""].slice(0, 5)
        : ["", "", "", "", ""]),
  }));

  const [roundWins, setRoundWins] = useState(draftState?.roundWins || makeEmptyRounds());
  const [roundTags, setRoundTags] = useState(
    normalizeStoredRoundTags(draftState?.roundTags)
  );
  const [roundTagInputs, setRoundTagInputs] = useState(makeEmptyRoundTagInputs());
  const [roundTagSuggestions, setRoundTagSuggestions] = useState([]);
  const [activeRoundTagInput, setActiveRoundTagInput] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]); // [{uid, username}]
  const [lineupContainer, setLineupContainer] = useState(null);
  const [selectedPlayerUids, setSelectedPlayerUids] = useState(draftState?.selectedPlayerUids || []);

  const [customAlert, setCustomAlert] = useState(null);

  const showAlert = (message, type = "info") => {
    setCustomAlert({
      message,
      type,
      position: "top",
    });
  };

  // ------- ROI (Region of Interest) state/refs -------
  const previewRef = useRef(null);
  const [roi, setRoi] = useState(null); // {x,y,w,h} in CLIENT px relative to preview
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef(null);
  const naturalSizeRef = useRef({ w: 0, h: 0 });

  // Tune these %s once by trial/error; they persist after you draw ROI once.
  const ROI_PRESET = { leftPct: 0.30, topPct: 0.00, widthPct: 0.40, heightPct: 0.14 };
  const HEADER_PRESET = { leftPct: 0.00, topPct: 0.00, widthPct: 0.38, heightPct: 0.22 };

  // Pre-fill once when dialog opens
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    if (!isEdit) {
      if (draftState) {
        setFields({
          us: draftState?.fields?.us ?? "",
          them: draftState?.fields?.them ?? "",
          opponent: draftState?.fields?.opponent ?? "",
          map: draftState?.fields?.map ?? linkedVideoMapId,
          gameType: draftState?.fields?.gameType ?? "Scrims",
          matchDateISO:
            draftState?.fields?.matchDateISO ?? (initialMatchDateISO || todayISO),
          comp:
            draftState?.fields?.comp ??
            (Array.isArray(linkedVideoComp)
              ? [...linkedVideoComp.slice(0, 5), "", "", "", "", ""].slice(0, 5)
              : ["", "", "", "", ""]),
        });

        setRoundWins(draftState?.roundWins || makeEmptyRounds());
        setRoundTags(normalizeStoredRoundTags(draftState?.roundTags));
        setRoundTagInputs(makeEmptyRoundTagInputs());
        setSelectedPlayerUids(draftState?.selectedPlayerUids || []);
      } else {
        setFields((f) => ({
          ...f,
          map: f.map || linkedVideoMapId,
          matchDateISO: initialMatchDateISO || todayISO,
          comp:
            Array.isArray(f.comp) && f.comp.length
              ? f.comp
              : Array.isArray(linkedVideoComp)
                ? [...linkedVideoComp.slice(0, 5), "", "", "", "", ""].slice(0, 5)
                : ["", "", "", "", ""],
        }));
        setRoundWins(makeEmptyRounds());
        setRoundTags(makeEmptyRoundTags());
        setRoundTagInputs(makeEmptyRoundTagInputs());
        setSelectedPlayerUids([]);
      }

      return;
    }

    const us = existing?.score?.us ?? "";
    const them = existing?.score?.them ?? "";
    const matchDate =
      existing?.matchDate?.toDate ? existing.matchDate.toDate() : existing?.matchDate || null;
    const iso = matchDate ? matchDate.toISOString().slice(0, 10) : todayISO;

    setFields({
      us: String(us),
      them: String(them),
      opponent: existing?.opponent || "",
      map: existing?.map || "",
      gameType: existing?.gameType || "Scrims",
      matchDateISO: iso,
      comp: Array.isArray(existing?.comp)
        ? [...existing.comp.slice(0, 5), "", "", "", "", ""].slice(0, 5)
        : ["", "", "", "", ""],
    });

    const players = Array.isArray(existing?.players) ? existing.players : [];
    setSelectedPlayerUids(players.map((p) => p.uid));

    const ex = existing?.roundWins || {};
    const next = makeEmptyRounds();
    next.atk.r1 = normalizeBoolOrNull(ex?.atk?.r1);
    next.atk.r2 = normalizeBoolOrNull(ex?.atk?.r2);
    next.atk.r3 = normalizeBoolOrNull(ex?.atk?.r3);
    next.def.r1 = normalizeBoolOrNull(ex?.def?.r1);
    next.def.r2 = normalizeBoolOrNull(ex?.def?.r2);
    next.def.r3 = normalizeBoolOrNull(ex?.def?.r3);
    setRoundWins(next);
    setRoundTags(normalizeStoredRoundTags(existing?.roundTags));
    setRoundTagInputs(makeEmptyRoundTagInputs());
  }, [isEdit, existing, todayISO, initialMatchDateISO, draftState, linkedVideoComp, linkedVideoMapId]);

  useEffect(() => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }

    if (isEdit) {
      setPreview(existing?.imageUrl || null);
      generatedFrameFileRef.current = null;
      return;
    }

    if (initialImageUrl) {
      setPreview(initialImageUrl);
      generatedFrameFileRef.current = null;
      return;
    }

    if (initialImageBlob) {
      const objectUrl = URL.createObjectURL(initialImageBlob);
      previewObjectUrlRef.current = objectUrl;
      setPreview(objectUrl);
      generatedFrameFileRef.current = blobToFile(initialImageBlob);
      return;
    }

    setPreview(null);
    generatedFrameFileRef.current = null;

    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
    };
  }, [isEdit, existing, initialImageUrl, initialImageBlob]);

  const [, setTeamRoles] = useState({}); // { [uid]: "Manager" | "Player" | ... }

  // --------- ROLE GROUPING (display by role, then alphabetical) ---------

  useEffect(() => {
    const loadMembers = async () => {
      if (!teamId) return;

      const teamSnap = await getDoc(doc(db, "teams", teamId));
      const data = teamSnap.exists() ? teamSnap.data() : {};
      const memberUids = Array.isArray(data?.members) ? data.members : [];
      const rolesMap = data?.teamRoles || {};

      const userDocs = await Promise.all(
        memberUids.map(async (uid) => {
          // Prefer the public uid->username directory (allowed by rules)
          const u = await getDoc(doc(db, "uids", uid));
          const username = u.exists() ? (u.data()?.username || uid) : uid;
          return { uid, username };
        })
      );

      // keep raw members list (we'll sort inside groups)
      setTeamMembers(userDocs);
      setTeamRoles(rolesMap);
    };

    loadMembers();
  }, [teamId]);

  useEffect(() => {
    const loadRoundTagSuggestions = async () => {
      if (!teamId) {
        setRoundTagSuggestions([]);
        return;
      }

      try {
        const matchesSnap = await getDocs(collection(db, "teams", teamId, "matches"));
        const suggestions = collectSuggestedRoundTags(
          matchesSnap.docs.map((matchDoc) => ({ id: matchDoc.id, ...matchDoc.data() }))
        );
        setRoundTagSuggestions(suggestions);
      } catch (error) {
        console.warn("Failed to load round tag suggestions:", error);
        setRoundTagSuggestions([]);
      }
    };

    loadRoundTagSuggestions();
  }, [teamId]);

  useEffect(() => {
    if (!onDraftChange) return;

    const nextPayload = {
      fields,
      roundWins,
      roundTags,
      selectedPlayerUids,
      linkedVideoMeta,
    };

    const serialized = JSON.stringify(nextPayload);

    if (lastDraftPayloadRef.current === serialized) return;

    lastDraftPayloadRef.current = serialized;
    onDraftChange(nextPayload);
  }, [fields, roundWins, roundTags, selectedPlayerUids, linkedVideoMeta, onDraftChange]);

  // Restore saved NAT ROI (if any) when a new preview is shown
  useEffect(() => {
    const saved = localStorage.getItem("tsd_roi_nat");
    if (saved && previewRef.current) {
      try {
        const nat = JSON.parse(saved);
        setTimeout(() => projectNaturalToClient(nat), 0);
      } catch {}
    }
  }, [preview]);

  function blobToFile(blob, name = "scoreboard-frame.png") {
    if (!blob) return null;
    return new File([blob], name, {
      type: blob.type || "image/png",
      lastModified: Date.now(),
    });
  }

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }

    generatedFrameFileRef.current = null;

    const url = URL.createObjectURL(f);
    previewObjectUrlRef.current = url;
    setPreview(url);
  };

  // ---------- ROI helpers ----------
  const onPreviewLoad = (e) => {
    const img = e.currentTarget;
    naturalSizeRef.current = { w: img.naturalWidth, h: img.naturalHeight };
    const saved = localStorage.getItem("tsd_roi_nat");
    if (saved) {
      try {
        projectNaturalToClient(JSON.parse(saved));
      } catch {}
    }
  };

  const projectNaturalToClient = (nat) => {
    const img = previewRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const sx = rect.width / (naturalSizeRef.current.w || 1);
    const sy = rect.height / (naturalSizeRef.current.h || 1);
    setRoi({
      x: Math.round(nat.x * sx),
      y: Math.round(nat.y * sy),
      w: Math.round(nat.w * sx),
      h: Math.round(nat.h * sy),
    });
  };

  // --- NEW: handle object-fit letterboxing (contain) correctly ---
  const getRenderedImageMetrics = () => {
    const img = previewRef.current;
    if (!img) return null;

    const rect = img.getBoundingClientRect();
    const naturalW = img.naturalWidth || 1;
    const naturalH = img.naturalHeight || 1;

    const rectAR = rect.width / rect.height;
    const natAR = naturalW / naturalH;

    let drawW, drawH, offsetX, offsetY;

    // Assume "contain"-style fitting inside the rect
    if (rectAR > natAR) {
      // container is wider than image -> image limited by height
      drawH = rect.height;
      drawW = drawH * natAR;
      offsetX = (rect.width - drawW) / 2;
      offsetY = 0;
    } else {
      // container is taller than image -> image limited by width
      drawW = rect.width;
      drawH = drawW / natAR;
      offsetX = 0;
      offsetY = (rect.height - drawH) / 2;
    }

    return { rect, drawW, drawH, offsetX, offsetY, naturalW, naturalH };
  };

  const clientToImageCoords = (clientX, clientY) => {
    const m = getRenderedImageMetrics();
    if (!m) return { x: 0, y: 0 };

    // convert mouse to coordinates inside the *drawn* image area
    const rawX = clientX - m.rect.left - m.offsetX;
    const rawY = clientY - m.rect.top - m.offsetY;

    // clamp to image area
    const x = Math.max(0, Math.min(m.drawW, rawX));
    const y = Math.max(0, Math.min(m.drawH, rawY));

    return { x, y };
  };

  const onMouseDown = (e) => {
    if (!previewRef.current) return;
    setIsDragging(true);
    const { x, y } = clientToImageCoords(e.clientX, e.clientY);
    dragStartRef.current = { x, y };
    setRoi({ x, y, w: 0, h: 0 });
  };

  const onMouseMove = (e) => {
    if (!isDragging || !dragStartRef.current) return;
    const { x, y } = clientToImageCoords(e.clientX, e.clientY);
    const sx = dragStartRef.current.x;
    const sy = dragStartRef.current.y;
    const rx = Math.min(sx, x);
    const ry = Math.min(sy, y);
    const rw = Math.abs(x - sx);
    const rh = Math.abs(y - sy);
    setRoi({ x: rx, y: ry, w: rw, h: rh });
  };

  const onMouseUp = () => {
    if (!roi || !previewRef.current) {
      setIsDragging(false);
      return;
    }
    setIsDragging(false);
    // Persist NAT ROI for next time
    const imgRect = previewRef.current.getBoundingClientRect();
    const sx = (naturalSizeRef.current.w || 1) / (imgRect.width || 1);
    const sy = (naturalSizeRef.current.h || 1) / (imgRect.height || 1);
    const nat = {
      x: Math.max(0, Math.round(roi.x * sx)),
      y: Math.max(0, Math.round(roi.y * sy)),
      w: Math.max(1, Math.round(roi.w * sx)),
      h: Math.max(1, Math.round(roi.h * sy)),
    };
    localStorage.setItem("tsd_roi_nat", JSON.stringify(nat));
  };

  const clearRoi = () => {
    setRoi(null);
    localStorage.removeItem("tsd_roi_nat");
  };

  // Quick preset (top bar): tweak percentages above, then persist nat ROI
  const setTopBarRoi = () => {
    const m = getRenderedImageMetrics();
    if (!m) return;

    // ROI dimensions relative to the *drawn image area*
    const x = Math.round(ROI_PRESET.leftPct * m.drawW);
    const y = Math.round(ROI_PRESET.topPct * m.drawH);
    const w = Math.round(ROI_PRESET.widthPct * m.drawW);
    const h = Math.round(ROI_PRESET.heightPct * m.drawH);

    setRoi({ x, y, w, h });

    // persist as NATURAL pixels
    const nat = {
      x: Math.round(x * (m.naturalW / m.drawW)),
      y: Math.round(y * (m.naturalH / m.drawH)),
      w: Math.round(w * (m.naturalW / m.drawW)),
      h: Math.round(h * (m.naturalH / m.drawH)),
    };

    localStorage.setItem("tsd_roi_nat", JSON.stringify(nat));
  };

  // ---------- OCR ----------
  const runOCR = async () => {
    const file = fileRef.current?.files?.[0] || generatedFrameFileRef.current;
    if (!file) return;
    setLoading(true);
    try {
      // Convert client ROI to NATURAL pixels, if present
      let cropNat = null;
      if (roi && previewRef.current && naturalSizeRef.current.w && naturalSizeRef.current.h) {
        // Convert ROI (in rendered-image coords) to NATURAL pixels
        if (roi && previewRef.current) {
          const m = getRenderedImageMetrics();
          if (m) {
            const sx = m.naturalW / m.drawW;
            const sy = m.naturalH / m.drawH;

            cropNat = {
              x: Math.max(0, Math.round(roi.x * sx)),
              y: Math.max(0, Math.round(roi.y * sy)),
              w: Math.max(1, Math.round(roi.w * sx)),
              h: Math.max(1, Math.round(roi.h * sy)),
            };
          }
        }
      }

      // Preprocess (downscale + grayscale + contrast) and crop if ROI set
      const scoreBase64 = await preprocessToBase64(
        file,
        { maxWidth: 1600, grayscale: true, contrast: 1.25, quality: 0.85 },
        cropNat
      );

      // Build a fixed top-left header crop (map + date)
      let headerCropNat = null;
      const m = getRenderedImageMetrics();
      if (m) {
        const x = Math.round(HEADER_PRESET.leftPct * m.drawW);
        const y = Math.round(HEADER_PRESET.topPct * m.drawH);
        const w = Math.round(HEADER_PRESET.widthPct * m.drawW);
        const h = Math.round(HEADER_PRESET.heightPct * m.drawH);

        headerCropNat = {
          x: Math.max(0, Math.round(x * (m.naturalW / m.drawW))),
          y: Math.max(0, Math.round(y * (m.naturalH / m.drawH))),
          w: Math.max(1, Math.round(w * (m.naturalW / m.drawW))),
          h: Math.max(1, Math.round(h * (m.naturalH / m.drawH))),
        };
      }

      const headerBase64 = await preprocessToBase64(
        file,
        { maxWidth: 1400, grayscale: true, contrast: 1.25, quality: 0.85 },
        headerCropNat
      );

      const call = httpsCallable(functions, "ocrScore");
      const res = await call({ imageBase64: scoreBase64, headerImageBase64: headerBase64 });
      const { text, parsed, meta, confidence: conf, debug, error } = res.data || {};
      const mapFromText = extractMapFromOcrText(text);
      const dateFromText = extractDateISOFromOcrText(text);
      const dateFromHeaderDebug = extractDateISOFromOcrText(debug?.headerNormalized || "");

      if (error) {
        console.error("OCR error:", error);
        showAlert(`OCR failed: ${error}. You can fill the score manually.`, "error");
        return;
      }

      setRaw(text || "");
      setOcrDebug(debug || null);
      setOcrParsed(parsed || null);
      setOcrMeta(meta || null);

      setFields((f) => {
        const next = { ...f };

        // 1) Prefer meta.map (from function)
        // 2) Fallback to map extracted from OCR text
        if (!setupEventIdRef.current && !String(next.map || "").trim()) {
          if (meta?.map) next.map = meta.map;
          else if (mapFromText) next.map = mapFromText;
        }

        // (We’ll handle date later when we tackle that issue)
        return next;
      });

      const fillFromParsed = (p) => {
        setFields((f) => ({
          ...f,
          us: p.us ?? "",
          them: p.them ?? "",
        }));
      };

      if (parsed) {
        fillFromParsed(parsed);
      } else {
        // --- CLIENT FALLBACK PARSE ---
        // If OCR text shows "6 DEFEAT 13" (or "6 VICTORY 13"), extract it here.
        const rawUpper = String(text || "")
          .normalize("NFKC")
          .replace(/[\u200B-\u200D\uFEFF]/g, "")
          .toUpperCase();

        const m = rawUpper.match(
          /\b(\d{1,2})\b\D{0,40}\b(VICTORY|DEFEAT|DRAW)\b\D{0,40}\b(\d{1,2})\b/
        );
        if (m) {
          const us = Number(m[1]);
          const them = Number(m[3]);
          if (!Number.isNaN(us) && !Number.isNaN(them)) {
            fillFromParsed({ us, them });
            setOcrParsed({ us, them }); // so UI shows Parsed correctly
          }
        }
      }

      // ✅ APPLY META (map + date)
      // - Map: fill only if empty (don’t overwrite what user typed)
      // - Date: overwrite the default "todayISO" with the OCR date, but don’t overwrite if user picked another date
      setFields((f) => {
        const next = { ...f };

        // MAP
        if (!setupEventIdRef.current && !String(next.map || "").trim()) {
          if (meta?.map) next.map = meta.map;
          else if (mapFromText) next.map = mapFromText;
        }

        // DATE
        const currentDate = String(next.matchDateISO || "").trim();

        const bestDate =
          meta?.matchDateISO ||
          dateFromHeaderDebug ||   // if your function sends debug.headerNormalized
          dateFromText;            // fallback when meta is null

        if (!setupEventIdRef.current && bestDate) {
          // If date is blank OR still defaulted to todayISO, replace with OCR date
          if (!currentDate || currentDate === todayISO) {
            next.matchDateISO = bestDate;
          }
        }

        return next;
      });

      setConfidence(conf ?? null);
    } catch (e) {
      console.error(e);
      showAlert("OCR failed. You can fill the score manually.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Players / Save ----------

  const buildPlayers = () =>
    selectedPlayerUids
      .map((uid) => {
        const found = teamMembers.find((m) => m.uid === uid);
        return found ? { uid: found.uid, username: found.username } : null;
      })
      .filter(Boolean);

  // NEW: round toggle handler
  const setRoundResult = (side, roundKey, didWin) => {
    setRoundWins((prev) => ({
      ...prev,
      [side]: {
        ...prev[side],
        [roundKey]: didWin,
      },
    }));
  };

  const setRoundTagInputValue = (side, roundKey, value) => {
    const inputKey = getRoundTagInputKey(side, roundKey);
    setRoundTagInputs((prev) => ({
      ...prev,
      [inputKey]: value,
    }));
  };

  const addRoundTags = (side, roundKey, incomingTags) => {
    const normalizedTags = normalizeRoundTagList(incomingTags);
    if (!normalizedTags.length) return;

    setRoundTags((prev) => {
      const nextList = normalizeRoundTagList([
        ...(prev?.[side]?.[roundKey] || []),
        ...normalizedTags,
      ]);

      return {
        ...prev,
        [side]: {
          ...prev[side],
          [roundKey]: nextList,
        },
      };
    });

    setRoundTagSuggestions((prev) =>
      normalizeRoundTagList([...(Array.isArray(prev) ? prev : []), ...normalizedTags]).sort(
        (a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })
      )
    );

    setRoundTagInputValue(side, roundKey, "");
  };

  const commitRoundTagInput = (side, roundKey) => {
    const rawValue = roundTagInputs?.[getRoundTagInputKey(side, roundKey)] || "";
    const parsedTags = splitRoundTagInput(rawValue);
    if (!parsedTags.length) return;
    addRoundTags(side, roundKey, parsedTags);
  };

  const removeRoundTag = (side, roundKey, tagToRemove) => {
    const removeKey = normalizeRoundTagValue(tagToRemove).toLowerCase();

    setRoundTags((prev) => ({
      ...prev,
      [side]: {
        ...prev[side],
        [roundKey]: (prev?.[side]?.[roundKey] || []).filter(
          (tag) => normalizeRoundTagValue(tag).toLowerCase() !== removeKey
        ),
      },
    }));
  };

  const getRoundTagSuggestions = (side, roundKey) => {
    const query = normalizeRoundTagValue(
      roundTagInputs?.[getRoundTagInputKey(side, roundKey)] || ""
    ).toLowerCase();
    if (!query) return [];

    const selected = new Set(
      (roundTags?.[side]?.[roundKey] || []).map((tag) =>
        normalizeRoundTagValue(tag).toLowerCase()
      )
    );

    return roundTagSuggestions
      .filter((tag) => !selected.has(normalizeRoundTagValue(tag).toLowerCase()))
      .filter((tag) =>
        !query || normalizeRoundTagValue(tag).toLowerCase().includes(query)
      )
      .slice(0, 6);
  };

  const saveMatch = async () => {
    if (saving) return;
    if (!setupReady) {showAlert("Save and select your event setup before saving stats.", "error"); return;}

    const selectedImageFile = fileRef.current?.files?.[0] || generatedFrameFileRef.current || null;
    const user = auth.currentUser;

    if (!user) {
      showAlert("Please sign in.", "error");
      return;
    }

    if (!teamId) {
      showAlert("No team selected.", "error");
      return;
    }

    if (selectedImageFile?.size >= 25 * 1024 * 1024) {showAlert("Team Stats screenshots must be smaller than 25 MB.", "error"); return;}
    setSaving(true);

    try {
      // Storage claims are only needed when uploading a new screenshot.
      if (selectedImageFile) {
        await ensureActiveTeamClaim(teamId);
        await user.getIdToken(true);
      }

      // uploader name
      let createdByUsername = user.displayName || user.uid;
      try {
        const uSnap = await getDoc(doc(db, "users", user.uid));
        if (uSnap.exists() && uSnap.data()?.username) {
          createdByUsername = uSnap.data().username;
        }
      } catch {}

      const us = String(fields.us).trim() === "" ? null : Number(fields.us);
      const them = String(fields.them).trim() === "" ? null : Number(fields.them);
      const result =
        us == null || them == null || Number.isNaN(us) || Number.isNaN(them)
          ? "T"
          : us === them
          ? "T"
          : us > them
          ? "W"
          : "L";

      const players = sharedPlayers || buildPlayers();
      const matchDate = fields.matchDateISO
        ? Timestamp.fromDate(new Date(`${fields.matchDateISO}T00:00:00`))
        : serverTimestamp();

      // optional image update
      let imageUrl = isEdit ? existing.imageUrl : null;
      let imageStoragePath = isEdit ? existing.imageStoragePath || null : null;
      let imageSizeBytes = isEdit ? existing.sizeBytes || null : null;

      // This supports BOTH:
      // 1) normal manual image upload via file input
      // 2) generated frame files passed in from video/live-record flows
      const imageFileToUpload =
        fileRef.current?.files?.[0] || generatedFrameFileRef.current || null;

      if (imageFileToUpload) {
        const safeName = imageFileToUpload.name.replace(/\s+/g, " ").trim();
        const path = `teams/${teamId}/matches/${Date.now()}-${safeName}`;
        const sref = ref(storage, path);

        try {
          const allowance = await checkTeamQuotaAllowance({
            teamId,
            action: "upload_image",
            bytesToAdd: imageFileToUpload.size || 0,
          });

          if (!allowance?.uploadAllowed) {
            showAlert(
              allowance?.message || "Uploading is unavailable right now.",
              "error"
            );
            return;
          }

          await uploadBytes(sref, imageFileToUpload, {
            customMetadata: {
              teamId,
              createdByUid: user.uid,
              sourceType: "team_stats_image",
              linkedMapId: linkedVideoMeta?.mapId || "",
              linkedVideoId: linkedVideoMeta?.videoId || "",
            },
          });

          const finalization = await confirmTrackedUploadFinalize({
            teamId,
            objectPath: sref.fullPath,
          });

          if (!finalization?.accepted) {
            throw new Error(
              finalization?.message || "Upload was rejected by the current team quota rules."
            );
          }

          imageUrl = await getDownloadURL(sref);
          imageStoragePath = sref.fullPath;
          imageSizeBytes = imageFileToUpload.size;
        } catch (err) {
          console.error("Team stats screenshot upload failed:", err);
          showAlert(
            err?.code === "storage/unauthorized"
              ? "You do not currently have permission to upload team screenshots for this team. Refresh the page and try again."
              : err?.message || "Failed to upload the screenshot.",
            "error"
          );
          return;
        }
      }

      // NEW: derived pistol fields (Round 1)
      const pistolAtkWin = normalizeBoolOrNull(roundWins?.atk?.r1);
      const pistolDefWin = normalizeBoolOrNull(roundWins?.def?.r1);
      const pistolWins = pistolCountFromRounds(roundWins); // 0..2

      const roundWinsPayload = {
        atk: {
          r1: normalizeBoolOrNull(roundWins?.atk?.r1),
          r2: normalizeBoolOrNull(roundWins?.atk?.r2),
          r3: normalizeBoolOrNull(roundWins?.atk?.r3),
        },
        def: {
          r1: normalizeBoolOrNull(roundWins?.def?.r1),
          r2: normalizeBoolOrNull(roundWins?.def?.r2),
          r3: normalizeBoolOrNull(roundWins?.def?.r3),
        },
      };

      const roundTagsPayload = mergePendingRoundTagInputs(roundTags, roundTagInputs);
      setRoundTags(roundTagsPayload);
      setRoundTagInputs(makeEmptyRoundTagInputs());

      let savedMatchId = existing?.id || savedStatsId || null;

      const cleanComp = Array.isArray(fields.comp)
        ? fields.comp.map((agent) => String(agent || "").trim()).filter(Boolean)
        : [];

      if (isEdit || savedStatsId) {
        await persistSetupStats(doc(db, "teams", teamId, "matches", existing?.id || savedStatsId), {
          opponent: fields.opponent || "",
          map: fields.map || "",
          score: { us, them },
          result,
          gameType: fields.gameType,
          players,
          comp: cleanComp,
          matchDate,
          editedAt: serverTimestamp(),

          // NEW
          roundWins: roundWinsPayload,
          roundTags: roundTagsPayload,
          pistolAtkWin,
          pistolDefWin,
          pistolWins,

          ...(imageUrl ? { imageUrl } : {}),
          ...(imageStoragePath ? { imageStoragePath } : {}),
          ...(imageSizeBytes != null ? { sizeBytes: imageSizeBytes } : {}),

          ...(linkedVideoMeta
            ? {
                linkedVideoMeta: {
                  mapId: linkedVideoMeta.mapId || "",
                  videoId: linkedVideoMeta.videoId || "",
                  matchId: linkedVideoMeta.matchId || "",
                  scoreboardCaptureSec:
                    typeof linkedVideoMeta.scoreboardCaptureSec === "number"
                      ? linkedVideoMeta.scoreboardCaptureSec
                      : null,
                  syncOffsetSec:
                    typeof linkedVideoMeta.syncOffsetSec === "number"
                      ? linkedVideoMeta.syncOffsetSec
                      : null,
                },
              }
            : {}),
        });
      } else {
        const createdRef = await persistSetupStats(doc(collection(db, "teams", teamId, "matches")), {
          createdAt: serverTimestamp(),
          createdBy: createdByUsername,
          createdByUid: user.uid,
          opponent: fields.opponent || "",
          map: fields.map || "",
          score: { us, them },
          result,
          gameType: fields.gameType,
          players,
          comp: cleanComp,
          matchDate,
          source: raw ? "ocr" : "manual",
          confidence: confidence ?? null,
          ocrRaw: raw || "",
          tags: [],
          imageUrl,

          // NEW
          roundWins: roundWinsPayload,
          roundTags: roundTagsPayload,
          pistolAtkWin,
          pistolDefWin,
          pistolWins,

          ...(imageStoragePath ? { imageStoragePath } : {}),
          ...(imageSizeBytes != null ? { sizeBytes: imageSizeBytes } : {}),

          ...(linkedVideoMeta
            ? {
                linkedVideoMeta: {
                  mapId: linkedVideoMeta.mapId || "",
                  videoId: linkedVideoMeta.videoId || "",
                  matchId: linkedVideoMeta.matchId || "",
                  scoreboardCaptureSec:
                    typeof linkedVideoMeta.scoreboardCaptureSec === "number"
                      ? linkedVideoMeta.scoreboardCaptureSec
                      : null,
                  syncOffsetSec:
                    typeof linkedVideoMeta.syncOffsetSec === "number"
                      ? linkedVideoMeta.syncOffsetSec
                      : null,
                },
              }
            : {}),
        });

        savedMatchId = createdRef.id;
      }

      // Write the reverse link back onto the video doc and map-level match doc
      // Write the reverse link back only if the target docs already exist.
      // In UploadVideoModal flow, pending video/match IDs are generated before the docs are created,
      // so reverse-linking must be skipped here and completed later when the upload succeeds.
      if (linkedVideoMeta?.mapId && linkedVideoMeta?.videoId && savedMatchId) {
        try {
          const videoDocRef = doc(
            db,
            "teams",
            teamId,
            "maps",
            linkedVideoMeta.mapId,
            "videos",
            linkedVideoMeta.videoId
          );

          const videoSnap = await getDoc(videoDocRef);

          if (videoSnap.exists()) {
            await updateDoc(videoDocRef, {
              teamStatsMatchId: savedMatchId,
              teamStatsImageUrl: imageUrl || null,
              teamStatsImageStoragePath: imageStoragePath || null,
              teamStatsLinkedAt: serverTimestamp(),
            });
          }
        } catch (err) {
          console.warn("Failed to reverse-link team stats to video doc:", err);
        }
      }

      if (linkedVideoMeta?.mapId && linkedVideoMeta?.matchId && savedMatchId) {
        try {
          const mapMatchDocRef = doc(
            db,
            "teams",
            teamId,
            "maps",
            linkedVideoMeta.mapId,
            "matches",
            linkedVideoMeta.matchId
          );

          const mapMatchSnap = await getDoc(mapMatchDocRef);

          if (mapMatchSnap.exists()) {
            await updateDoc(mapMatchDocRef, {
              teamStatsMatchId: savedMatchId,
              teamStatsImageUrl: imageUrl || null,
              teamStatsImageStoragePath: imageStoragePath || null,
              teamStatsLinkedAt: serverTimestamp(),
            });
          }
        } catch (err) {
          console.warn("Failed to reverse-link team stats to map match doc:", err);
        }
      }

      onSaved?.({
        id: savedMatchId,
        imageUrl,
        imageStoragePath,
        linkedVideoMeta,
      });
      triggerTeamQuotaRefresh(teamId);
      onClose();
    } catch (err) {
      console.error("Failed to save team statistics:", err);
      showAlert(
        err?.message || "Failed to save match statistics. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const captureTimeText =
  typeof linkedVideoMeta?.scoreboardCaptureSec === "number"
    ? formatCaptureTime(linkedVideoMeta.scoreboardCaptureSec)
    : "";

  // UI text helper
  const pillText = (v) => (v === true ? "W" : v === false ? "L" : "—");

  return (
    <div className="ts-dialog__backdrop">
      <div className="ts-dialog">
        <button
          type="button"
          className="ts-dialog__close ts-btn-upsc ts-btn-upsc--cancel app-cancel-control"
          onClick={onClose}
          disabled={saving}
          aria-label="Close"
          title="Close"
        >
          ×
        </button>

        <h2 className="ts-dialog__title">
          {isEdit ? "Edit Match Statistics" : "Upload Match Statistics"}
        </h2>

        <EventSetup onAttachExisting={existing?.id ? onAttachExisting : undefined} attachExistingLabel="Attach existing VOD" lineupContainer={lineupContainer} lineupSeed={existing || {comp: fields.comp, players: buildPlayers()}} lifecycle={setupLifecycle} lockSelection={!!initialSetupEventId} teamId={teamId} selectedEventId={setupEventId} onReadyChange={setSetupReady} disabled={saving}
          onSelectEvent={(id, event) => {setSetupEventId(id || ""); setSharedPlayers(event?.players || null); if (event) setFields(current => ({...current,
            comp: event.comp || current.comp, map: event.mapName || "", opponent: event.opponentName || "", matchDateISO: event.startUTC?.slice(0, 10) || current.matchDateISO,
            gameType: event.tag === "Scrim" ? "Scrims" : event.tag === "Game Day" ? "Official Match" : ["Premier", "Ranked"].includes(event.tag) ? event.tag : current.gameType}));}}/>

        {!isEdit && captureTimeText && (
          <p className="ts-dialog__capture-time">
            Selected video time: <strong>{captureTimeText}</strong>
          </p>
        )}

        {hideFileInput && preview && (
          <p className="ts-help">
            This is the exact frame that was selected from the video for match team stats. You can auto-analyze this image or fill the stats manually.
          </p>
        )}

        {!hideFileInput && (
          <input
            id="tsd-file"
            name="screenshot"
            type="file"
            accept="image/*"
            ref={fileRef}
            onChange={handleFile}
          />
        )}

        {preview && (
          <div className="ts-preview-wrap">
            <img
              className="ts-dialog__preview"
              src={preview}
              alt="preview"
              ref={previewRef}
              onLoad={onPreviewLoad}
              onError={() => {
                setPreview(null);
              }}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
              draggable={false}
              style={{ userSelect: "none" }}
            />
            {roi && (() => {
              const m = getRenderedImageMetrics();
              const ox = m ? m.offsetX : 0;
              const oy = m ? m.offsetY : 0;

              return (
                <div
                  className="ts-ocr-overlay"
                  style={{
                    left: `${roi.x + ox}px`,
                    top: `${roi.y + oy}px`,
                    width: `${roi.w}px`,
                    height: `${roi.h}px`,
                  }}
                />
              );
            })()}
          </div>
        )}

        {preview && (
          <>
            <div className="ts-ocr-tools">
              <button
                type="button"
                className="ts-btn-upsc ts-btn-upsc--ghost"
                onClick={setTopBarRoi}
              >
                Use Top Bar
              </button>

              <button
                type="button"
                className="ts-btn-upsc ts-btn-upsc--ghost"
                onClick={clearRoi}
              >
                Clear Region
              </button>

              {!isEdit && (
                <button
                  type="button"
                  className="ts-btn-upsc"
                  onClick={runOCR}
                  disabled={loading}
                >
                  {loading ? "Reading…" : "Auto-Read Score"}
                </button>
              )}
            </div>

            <p className="ts-ocr-instructions">
              Tip: Click–drag on the image to mark the exact scoreboard area. That highlighted box is what OCR reads.
            </p>
          </>
        )}

        <div className="ts-form">
          <div className="ts-field">
            <label htmlFor="tsd-us">Our Score</label>
            <input
              id="tsd-us"
              name="us"
              value={fields.us}
              inputMode="numeric"
              onChange={(e) => setFields((f) => ({ ...f, us: e.target.value }))}
            />
          </div>

          <div className="ts-field">
            <label htmlFor="tsd-them">Their Score</label>
            <input
              id="tsd-them"
              name="them"
              value={fields.them}
              inputMode="numeric"
              onChange={(e) => setFields((f) => ({ ...f, them: e.target.value }))}
            />
          </div>

          {/* NEW: ATK/DEF Round Toggles */}
          <div className="ts-field ts-field--full">
            <label>Pistols + early rounds (ATK / DEF)</label>
            <StatsVideoReference src={referenceVideoUrl} initialTime={linkedVideoMeta?.scoreboardCaptureSec||0}/>

            <div className="ts-rounds-wrap">
              {["atk", "def"].map((side) => (
                <div key={side} className="ts-rounds-block">
                  <div className="ts-rounds-block__head">
                    <span className="ts-rounds-block__title">{side === "atk" ? "ATK" : "DEF"}</span>
                    <span className="ts-rounds-block__mini">
                      Pistol: <strong>{pillText(roundWins?.[side]?.r1)}</strong>
                    </span>
                  </div>

                  <div className="ts-rounds-grid">
                    {ROUND_SLOT_DEFS.map((r) => {
                      const val = roundWins?.[side]?.[r.key];
                      const currentTags = roundTags?.[side]?.[r.key] || [];
                      const currentInputKey = getRoundTagInputKey(side, r.key);
                      const currentSuggestions = getRoundTagSuggestions(side, r.key);
                      return (
                        <div key={r.key} className="ts-round-cell">
                          <div className="ts-round-label">{r.label}</div>

                          <div className="ts-round-toggle">
                            <button
                              type="button"
                              className={`ts-round-btn ts-round-btn--win ${val === true ? "is-active" : ""}`}
                              onClick={() => setRoundResult(side, r.key, true)}
                            >
                              W
                            </button>
                            <button
                              type="button"
                              className={`ts-round-btn ts-round-btn--loss ${val === false ? "is-active" : ""}`}
                              onClick={() => setRoundResult(side, r.key, false)}
                            >
                              L
                            </button>
                          </div>

                          <div className="ts-round-tags">
                            {currentTags.length > 0 && (
                              <div className="ts-round-tag-list">
                                {currentTags.map((tag) => (
                                  <span key={tag} className="ts-round-tag-chip">
                                    <span>{tag}</span>
                                    <button
                                      type="button"
                                      className="ts-round-tag-chip__remove app-cancel-control"
                                      aria-label={`Remove ${tag}`}
                                      onClick={() => removeRoundTag(side, r.key, tag)}
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}

                            <textarea
                              id={`ts-round-tag-${side}-${r.key}`}
                              name={`roundTag-${side}-${r.key}`}
                              className="ts-round-tag-input"
                              value={roundTagInputs?.[currentInputKey] || ""}
                              placeholder={r.strategyPlaceholder}
                              rows={2}
                              autoComplete="off"
                              spellCheck={false}
                              onChange={(e) =>
                                setRoundTagInputValue(side, r.key, e.target.value)
                              }
                              onFocus={() => setActiveRoundTagInput(currentInputKey)}
                              onBlur={() => {
                                window.setTimeout(() => {
                                  setActiveRoundTagInput((prev) =>
                                    prev === currentInputKey ? null : prev
                                  );
                                }, 120);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === ",") {
                                  e.preventDefault();
                                  commitRoundTagInput(side, r.key);
                                }
                              }}
                            />

                            {activeRoundTagInput === currentInputKey &&
                              currentSuggestions.length > 0 && (
                                <div className="ts-round-tag-suggestions">
                                  {currentSuggestions.map((tag) => (
                                    <button
                                      key={tag}
                                      type="button"
                                      className="ts-round-tag-suggestion"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => addRoundTags(side, r.key, [tag])}
                                    >
                                      {tag}
                                    </button>
                                  ))}
                                </div>
                              )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="ts-round-hint">
              Optional: add strategy tags per round. Press Enter or comma to commit, or just save and any typed tags will be included automatically.
            </div>

            <div className="ts-pistol-summary">
              <span className="ts-pistol-summary__label">Pistol score:</span>
              <span className="ts-pistol-summary__value">{pistolCountFromRounds(roundWins)}/2</span>
            </div>


          </div>

          <div className="ts-field ts-field--full EventSetup" ref={setLineupContainer}/>
        </div>

        {raw && !isEdit && (
          <details className="ts-details">
            <summary>OCR details</summary>
            <p>Function version: {ocrDebug?.version ?? "n/a"}</p>
            <p>Confidence: {confidence ?? "n/a"}</p>
            <p>Parsed: {ocrParsed ? `Us=${ocrParsed.us}, Them=${ocrParsed.them}` : "null"}</p>
            <p>
              Meta:{" "}
              {ocrMeta
                ? `Map=${ocrMeta.map ?? "null"}, Date=${ocrMeta.matchDateISO ?? "null"}`
                : "null"}
            </p>

            <pre className="ts-pre">{raw}</pre>

            {ocrDebug?.normalized && (
              <>
                <p><strong>Normalized:</strong></p>
                <pre className="ts-pre">{ocrDebug.normalized}</pre>
              </>
            )}

            {ocrDebug?.headerNormalized && (
              <>
                <p><strong>Header Normalized:</strong></p>
                <pre className="ts-pre">{ocrDebug.headerNormalized}</pre>
              </>
            )}

            {Array.isArray(ocrDebug?.tokens) && ocrDebug.tokens.length > 0 && (
              <>
                <p><strong>Tokens (OCR order):</strong></p>
                <pre className="ts-pre">
                  {ocrDebug.tokens
                    .map((t, i) => {
                      const c = typeof t.confidence === "number" ? t.confidence.toFixed(3) : "n/a";
                      const b = t.bbox || {};
                      return `${i + 1}. ${t.text}  [conf:${c}]  bbox: (${b.xMin},${b.yMin})-(${b.xMax},${b.yMax})`;
                    })
                    .join("\n")}
                </pre>
              </>
            )}

          </details>
        )}

        <div className="ts-actions">
          <button
            className={`ts-btn-upsc ${saving ? "ts-btn-upsc--loading" : ""}`}
            onClick={saveMatch}
            disabled={saving}
          >
            <span className="ts-btn-upsc__content">
              {saving && <span className="ts-btn-upsc__spinner" aria-hidden="true" />}
              <span>
                {saving
                  ? "Saving Statistics..."
                  : isEdit
                  ? "Save Match Statistics Changes"
                  : "Save Match Statistics"}
              </span>
            </span>
          </button>
          <button
            className="ts-btn-upsc ts-btn-upsc--cancel app-cancel-control"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </div>
      {customAlert && (
        <CustomAlert
          message={customAlert.message}
          type={customAlert.type}
          position={customAlert.position}
          onClose={() => setCustomAlert(null)}
        />
      )}
    </div>
  );
}
